import { CallLambda } from "redux/actions/CallLambda";
import { SaveObjectAction } from "redux/actions/SaveObject";
import Config from "redux/classes/Config";
import Model from "redux/classes/Model";
import Session from "redux/classes/Session";
import { AppDispatch } from "redux/store";
import { parse } from "papaparse";
import { JobInfo, JobState, JobOutputItem, JobOutputData, JobOutputPaths, LoadJobsResult, LoadJobStatesResult, JobFeedback, JobFeedbackItem, LoadJobFeedbackResult } from "./NLIJobService";

export type DashJobOutput = Readonly<{
    Data1?: JobOutputItem<JobOutputData>;
    Data2?: JobOutputItem<JobOutputData>;
    Data3?: JobOutputItem<JobOutputData>;
    Data4?: JobOutputItem<JobOutputData>;
    Data5?: JobOutputItem<JobOutputData>;
    Data6?: JobOutputItem<JobOutputData>;
    Data7?: JobOutputItem<JobOutputData>;
    Data8?: JobOutputItem<JobOutputData>;
    Data9?: JobOutputItem<JobOutputData>;
    Data10?: JobOutputItem<JobOutputData>;
    Data11?: JobOutputItem<JobOutputData>;
    Data12?: JobOutputItem<JobOutputData>;
    Data13?: JobOutputItem<JobOutputData>;
    Data14?: JobOutputItem<JobOutputData>;
    Data15?: JobOutputItem<JobOutputData>;
    Data16?: JobOutputItem<JobOutputData>;
    Data17?: JobOutputItem<JobOutputData>;
    Data18?: JobOutputItem<JobOutputData>;
    Data19?: JobOutputItem<JobOutputData>;
    Data20?: JobOutputItem<JobOutputData>;
    Data21?: JobOutputItem<JobOutputData>;
    Data22?: JobOutputItem<JobOutputData>;
    Data23?: JobOutputItem<JobOutputData>;
    Data24?: JobOutputItem<JobOutputData>;
    Formatting?: JobOutputItem<{ [key: number]: number }>;
    Error?: JobOutputItem<string>;
}>;

export type DashJobOutputPaths = Readonly<{
  Data1?: string;
  Data2?: string;
  Data3?: string;
  Data4?: string;
  Data5?: string;
  Data6?: string;
  Data7?: string;
  Data8?: string;
  Data9?: string;
  Data10?: string;
  Data11?: string;
  Data12?: string;
  Data13?: string;
  Data14?: string;
  Data15?: string;
  Data16?: string;
  Data17?: string;
  Data18?: string;
  Data19?: string;
  Data20?: string;
  Data21?: string;
  Data22?: string;
  Data23?: string;
  Data24?: string;
  Formatting?: string;
  Error?: string;
}>;

export class DashJobService {

    constructor(
      public dispatch: AppDispatch,
      public session: Session,
      public model: Model,
      public userID: string) {

    }

    async loadJobs(): Promise<LoadJobsResult> {
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", { action: "loadJobs", modelID: this.model.modelID }));
      if (status !== 200) {
          throw new Error("Failed loading previous questions");
      }
      return {
          serverTime: Date.parse(data.serverTime),
          jobs: data.jobs.map((job: any) => ({
              jobID: job.job_id,
              alias: job.alias,
              question: job.description,
              startTime: Date.parse(job.start_time),
              endTime: job.end_time ? Date.parse(job.end_time) : undefined,
              status: job.job_status,
              statusMessage: job.running_message || "",
              feedback: job.feedback,
              tags: job.tags ? Object.values(job.tags) : [],
              replyTarget: job.reply_target,
              scheduleTime: job.schedule_time || undefined,
              scheduleType: job.schedule_type || undefined
          })).sort((a: JobInfo, b: JobInfo) => b.startTime - a.startTime)
      };
    }

    async loadJobStates(): Promise<LoadJobStatesResult> {
      // Light polling Query — same shape as NLIJobService.loadJobStates.
      // Returns polling-relevant fields for every in-scope job
      // (cust_id + model_id + user_id). The caller diffs IDs against
      // its in-memory list to detect new jobs and deletions, and follows
      // up with loadJobsByIDs for any new IDs.
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", {
        action: "loadJobStates",
        modelID: this.model.modelID,
      }));
      if (status !== 200) {
          throw new Error("Failed refreshing job information");
      };
      return {
        serverTime: Date.parse(data.serverTime),
        jobStates: data.jobStates.map((jobState: any) => ({
          jobID: jobState.job_id,
          status: jobState.job_status,
          statusMessage: jobState.running_message,
          endTime: jobState.end_time ? Date.parse(jobState.end_time) : undefined,
          alias: jobState.alias
        }))
      }
    }

    async loadJobsByIDs(jobIDs: readonly string[]): Promise<LoadJobsResult> {
      // Targeted full-row fetch — used as a follow-up to loadJobStates
      // when new IDs are surfaced. Returns the same JobInfo shape as loadJobs.
      if (jobIDs.length === 0) {
        return { serverTime: Date.now(), jobs: [] };
      }
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", {
        action: "loadJobsByIDs",
        jobIDs: jobIDs,
      }));
      if (status !== 200) {
        throw new Error("Failed loading new jobs");
      }
      return {
        serverTime: Date.parse(data.serverTime),
        jobs: data.jobs.map((job: any) => ({
          jobID: job.job_id,
          alias: job.alias,
          question: job.description,
          startTime: Date.parse(job.start_time),
          endTime: job.end_time ? Date.parse(job.end_time) : undefined,
          status: job.job_status,
          statusMessage: job.running_message || "",
          feedback: job.feedback,
          tags: job.tags ? Object.values(job.tags) : [],
          replyTarget: job.reply_target,
          scheduleTime: job.schedule_time || undefined,
          scheduleType: job.schedule_type || undefined
        }))
      };
    }

    async loadJobOutput(jobID: string, cachedOutputPaths?: DashJobOutputPaths): Promise<DashJobOutput> {

      let outputPaths: {[key: string]: string};
      if (cachedOutputPaths) {
        outputPaths = cachedOutputPaths as {[key: string]: string};
      } else {
        const payload = JSON.stringify({
          "model": this.model.alias,
          "jobID": jobID,
        })

        const {status, data} = await this.dispatch(CallLambda(this.session, "jobLoader", payload))

        if (status !== 200) {
          throw new Error("Failed loading job details");
        }

        const outputs = JSON.parse(data.config).outputs;
        const errorPath = {
          'Error': 'error.err'
        }
        outputPaths = { ...outputs, ...errorPath };
      }

      const payload = JSON.stringify({
        "model": this.model.alias,
        "jobID": jobID,
        "outputs": Object.values(outputPaths),
        "status": "REVIEW"
      });

      const {status, data} = await this.dispatch(CallLambda(this.session, "resultLoader", payload));

      if (status !== 200) {
        throw new Error("Failed loading job results");
      }

      const results = data.results;

      const jobOutput: any = {};

      // Helper function to parse CSV data into JobOutputData format (PapaParse handles quoted fields with newlines)
      const parseCSVToJobOutputData = (data: string): JobOutputData => {
        const parsed = parse(data, { header: false });
        const rows = (parsed.data as string[][]).filter((row: string[]) => row.some((cell) => cell != null && String(cell).trim() !== ""));
        if (rows.length === 0) {
          return { columns: [], dataSource: [] };
        }

        const dataRows = rows.slice(1).map((row: string[]) => row.map((cell: string) => cell));

        const columnMetadata = rows[0].map(() => ({
          type: "text",
          maxDecimals: 0
        })) as Array<{type: "text"|"number", maxDecimals: number}>;

        const columns = rows[0].map((header: string, colIndex: number) => {
          let colName = header || "";
          const prefix = colName.substring(0,1);
          let align = "left";
          let maxDecimals = 0;
          switch (prefix) {
            case "#":
              columnMetadata[colIndex].type = "number";
              align = "right";
              colName = colName.substring(1);
              dataRows.forEach((row: string[]) => {
                const s = row[colIndex]?.trim();
                if (s) {
                  let decimals = 0;
                  const dotIndex = s.indexOf(".");
                  if (dotIndex >= 0) {
                    decimals = s.length - dotIndex - 1;
                    if (decimals > maxDecimals) {
                      maxDecimals = decimals;
                    }
                  }
                }
              });
              columnMetadata[colIndex].maxDecimals = maxDecimals;
              break;
            case "$":
            case "&":
            case "%":
              colName = colName.substring(1);
              break;
          }
          return {
            title: colName,
            dataIndex: colIndex.toString(),
            key: colIndex.toString(),
            align: align as "left" | "right" | "center"
          };
        });

        return {
          columns: columns,
          dataSource: dataRows.map((row: string[], rowIndex: number) => {
            const dataItem: { key: string, [dataIndex: string]: string } = {
              key: "row" + rowIndex
            };
            rows[0].forEach((column: string, colIndex: number) => {
              let textValue = row[colIndex];
              const meta = columnMetadata[colIndex];
              if (meta.type === "number") {
                const n = parseFloat(textValue);
                if (!isNaN(n)) {
                  const maximumFractionDigits = 6;
                  const minimumFractionDigits = Math.min(meta.maxDecimals, maximumFractionDigits);
                  textValue = n.toLocaleString(undefined, {minimumFractionDigits, maximumFractionDigits: minimumFractionDigits});
                }
              }
              dataItem[colIndex.toString()] = textValue;
            });
            return dataItem;
          })
        } as JobOutputData;
      };

      for (let path in results) {
          let parts = path.split(".");
          let name = parts.slice(0, parts.length - 1).join(".");
          let ext = parts[parts.length - 1];
          const label = Object.keys(outputPaths).find(k => outputPaths[k] === path) as string;

          let data: any;

          try {
            if (label === "Error") {
              data = atob(results[path]).trim();
              jobOutput.Error = {
                name: name,
                extension: ext,
                content: data
              };
              continue;
            }

            const url = atob(results[path]);
            const response = await fetch(url);
            if (!response.ok) throw new Error('Failed to fetch content');
            data = (await response.text()).trim();

            // Handle Data 1-24 outputs
            if (label && label.startsWith("Data ")) {
              const dataIndex = parseInt(label.substring(5));
              if (!isNaN(dataIndex) && dataIndex >= 1 && dataIndex <= 24) {
                const parsedData = parseCSVToJobOutputData(data);
                const dataKey = `Data${dataIndex}` as keyof DashJobOutput;
                (jobOutput as any)[dataKey] = {
                  name: name,
                  extension: ext,
                  content: parsedData
                };
              }
            }
            // Handle Formatting JSON
            else if (label === "Formatting") {
              try {
                const formattingData = JSON.parse(data);
                jobOutput.Formatting = {
                  name: name,
                  extension: ext,
                  content: formattingData
                };
              } catch (error) {
                console.error('Error parsing formatting JSON:', error);
              }
            }
          } catch (error) {
            console.error('Error fetching content:', error);
            continue;
          }
      }
      return jobOutput as DashJobOutput;
    }

    async createJob(modelAlias: string, sqlQueries: string[], sizes: number[], alias?: string, tags?: string[], dimensions?: number, jobID?: string): Promise<JobInfo> {

      let date = new Date()
      let newJobID = jobID || date.toISOString()
      let jobAlias = alias || `${this.model.alias}-${newJobID}`

      // Build parameters object with SQL 1-24, Size 1-24, and overall Dimensions
      const parameters: {[key: string]: any} = {};
      for (let i = 0; i < 24; i++) {
        parameters[`SQL ${i + 1}`] = sqlQueries[i] || "";
        parameters[`Size ${i + 1}`] = sizes[i] || 0;
      }
      parameters["Dimensions"] = dimensions || 0;

      // Build outputs object with Data 1-24 and Formatting
      const outputs: {[key: string]: string} = {};
      for (let i = 1; i <= 24; i++) {
        outputs[`Data ${i}`] = `data_${i}.csv`;
      }
      outputs["Formatting"] = "formatting_instructions.json";

      // Use the alias (dashboard title) as the description
      const description = alias || "Dashboard";

      let config = new Config(
        {
          "userID": this.userID,
          "alias": jobAlias,
          "description": description
        },
        parameters,
        {},
        outputs
      );

      // Always upload config to S3 to avoid CloudFront/API Gateway size limits
      const configJSON = config.makeJSON();
      const configBlob = new Blob([configJSON], { type: 'application/json' });
      const configBaseName = `${this.model.alias}_config_${newJobID}.json`;
      const configRelativePath = `config/${configBaseName}`;

      // Get presigned URL for config upload
      const uploadPayload = JSON.stringify({
        "model": this.model.alias,
        "file": configBaseName,
        "size": configBlob.size.toString(),
        "type": "application/json",
        "target": "config"
      });

      const uploadResponse = await this.dispatch(CallLambda(this.session, "fileUploader", uploadPayload));

      if (uploadResponse.status !== 200) {
        throw new Error("Failed to get presigned URL for config upload");
      }

      const presignedUrl = uploadResponse.data.presignedURL;
      const uploadResult = await fetch(presignedUrl, {
        method: 'PUT',
        body: configBlob,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!uploadResult.ok) {
        throw new Error("Failed to upload config to S3");
      }

      const finalPayload = JSON.stringify({
        "model": this.model.alias,
        "configS3Path": configRelativePath,
        "jobID": newJobID,
      });

      const {status, data} = await this.dispatch(CallLambda(this.session, "jobCreator", finalPayload))

      if (status !== 200) {
        throw new Error("Failed creating job");
      }
      const payload2 = JSON.stringify({
        "model": this.model.alias,
        "jobID": newJobID,
        "jobAlias": jobAlias,
        "jobDesc": description,
        "modelID": this.model.modelID,
        "launchType": this.model.launchType,
        "replyTarget": undefined
      })

      const response = await this.dispatch(CallLambda(this.session, "modelInitializer", payload2))

      if (response.status !== 200) {
        throw new Error("Failed initializing job");
      };

      // Apply tags if provided (after job is created)
      if (tags && tags.length > 0) {
        try {
          await this.addJobTags(newJobID, tags);
        } catch (error) {
          // Log error but don't fail job creation if tags can't be added
          console.error("Failed to add tags to job:", error);
        }
      }

      const {status: loadStatus, data: loadedData} = await this.dispatch(CallLambda(this.session, "nliService", { action: "loadJob", jobID: newJobID }));
      if (loadStatus !== 200) {
        throw new Error("Failed loading new job");
      }

      const job = loadedData.job;
      return {
          jobID: job.job_id,
          alias: job.alias,
          question: job.description,
          startTime: Date.parse(job.start_time),
          status: job.job_status,
          statusMessage: job.running_message || "",
          tags: job.tags ? Object.values(job.tags) : [],
          replyTarget: job.reply_target
      };
    }

    async deleteJob(jobID: string): Promise<void> {
      const {status, data} = await this.dispatch(CallLambda(this.session, "jobDeleter", { jobID: jobID }));
      if (status !== 200) {
          throw new Error("Failed deleting job");
      }
    }

    async scheduleJob(jobID: string, scheduleTime: string | null, scheduleType: string | null): Promise<void> {
      const {status, data} = await this.dispatch(CallLambda(this.session, "jobScheduler", {
        jobID: jobID,
        scheduleTime: scheduleTime,
        scheduleType: scheduleType
      }));
      if (status !== 200) {
          throw new Error("Failed scheduling job");
      }
    }

    async deleteJobsByReplyTarget(jobID: string, deleteSourceJob: boolean = false): Promise<void> {
      const replyTarget = `${this.model.alias}-${jobID}`;
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", { action: "deleteJobsByReplyTarget", replyTarget: replyTarget, deleteSourceJob: deleteSourceJob }));
      if (status !== 200) {
          throw new Error("Failed deleting jobs by reply target");
      }
    }

    download(jobID: string, name: string, extension: string) {
      this.dispatch(SaveObjectAction(this.session, this.model.alias, jobID, name, extension));
    }

    async getJobParameters(jobID: string): Promise<{ [key: string]: any }> {
      const payload = JSON.stringify({
        "model": this.model.alias,
        "jobID": jobID,
      })

      const {status, data} = await this.dispatch(CallLambda(this.session, "jobLoader", payload))

      if (status !== 200) {
        throw new Error("Failed loading job details");
      }

      const config = JSON.parse(data.config);
      return config.parameters || {};
    }

    async regenerateJob(jobID: string): Promise<JobInfo> {
      // Fetch the existing job to get its alias, tags, and parameters
      const {status: loadStatus, data: loadedData} = await this.dispatch(CallLambda(this.session, "nliService", { action: "loadJob", jobID: jobID }));
      if (loadStatus !== 200) {
          throw new Error("Failed loading job for regeneration");
      }
      const existingJob = loadedData.job;
      const existingAlias = existingJob.alias;
      const existingTags = existingJob.tags ? Object.values(existingJob.tags) as string[] : [];

      // Get job parameters (SQL queries, sizes, dimensions)
      const parameters = await this.getJobParameters(jobID);

      // Extract SQL queries and sizes from parameters
      const sqlQueries: string[] = [];
      const sizes: number[] = [];
      for (let i = 1; i <= 24; i++) {
        sqlQueries.push(parameters[`SQL ${i}`] || "");
        sizes.push(parseInt(parameters[`Size ${i}`]) || 0);
      }

      // Extract dimensions
      const dimensions = parseInt(parameters["Dimensions"]) || 0;

      // Delete the existing job
      await this.deleteJob(jobID);

      // Create a new job with the same parameters, alias, tags, and ID
      return await this.createJob(this.model.alias, sqlQueries, sizes, existingAlias, existingTags, dimensions, jobID);
    }

    async regenerateJobWithParams(jobID: string, sqlQueries: string[], sizes: number[], alias?: string, dimensions?: number): Promise<JobInfo> {
      // Fetch the existing job to get its tags and schedule
      const {status: loadStatus, data: loadedData} = await this.dispatch(CallLambda(this.session, "nliService", { action: "loadJob", jobID: jobID }));
      if (loadStatus !== 200) {
          throw new Error("Failed loading job for regeneration");
      }
      const existingJob = loadedData.job;
      const existingTags = existingJob.tags ? Object.values(existingJob.tags) as string[] : [];
      const existingScheduleTime = existingJob.schedule_time || null;
      const existingScheduleType = existingJob.schedule_type || null;

      // Delete the existing job
      await this.deleteJob(jobID);

      // Create a new job with the new parameters, alias, and tags
      const newJobInfo = await this.createJob(this.model.alias, sqlQueries, sizes, alias, existingTags, dimensions, jobID);

      // Preserve the schedule if it existed
      if (existingScheduleTime) {
          await this.scheduleJob(newJobInfo.jobID, existingScheduleTime, existingScheduleType);
      }

      return newJobInfo;
    }

    async addJobTags(jobID: string, tags: string[]) {
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", { action: "addJobTags", jobID: jobID, tags: tags }));
      if (status !== 200) {
          throw new Error("Failed saving tags");
      }
    }

    async removeJobTags(jobID: string, tags: string[]) {
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", { action: "deleteJobTags", jobID: jobID, tags: tags }));
      if (status !== 200) {
          throw new Error("Failed saving tags");
      }
    }

    async updateJobAlias(jobID: string, alias: string): Promise<void> {
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", { action: "updateJobAlias", jobID: jobID, alias: alias }));
      if (status !== 200) {
          throw new Error("Failed updating job alias");
      }
    }

    async loadJobFeedback(cursor: any, minResults: number): Promise<LoadJobFeedbackResult> {
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", { action: "loadJobFeedback", modelID: this.model.modelID, cursor: cursor, minResults: minResults }));
      if (status !== 200) {
          throw new Error("Failed loading feedback");
      }
      return {
        cursor: data.cursor,
        items: data.items.map((item: any) => ({
          jobID: item.job_id,
          jobStartTime: item.job_start_time,
          feedbackTime: item.feedback_time,
          userID: item.user_id,
          question: item.question,
          rating: item.rating,
          comment: item.comment,
          outputs: item.outputs
        }))
      };
    }
}

